"use client";

import { getFormOneDataFromLocal } from "@/lib/edcrypt";
import React, { useCallback, useRef, useState } from "react";
import PopupForm from "@/components/newPageComponent/CommonSection/PopupForm";
import { cards as cmaCardsRaw } from "@/components/newPageComponent/cma-new/main/new/AboutCMA";

import {
  isLogo,
  logoSrc,
  logoSrcSet,
  resolveLogoHeight,
  LOGO_IMG_CLASS,
  LOGO_SLOT_CLASS,
  type LogoSizing,
} from "@/components/newPageComponent/CommonSection/companyLogo";

const LOCAL_STORE_NAME = "MILES_NEXT_F1_REF_ENQUIRY";

export type TestimonialCard = {
  name: string;
  designation: string;
  /** Logo URL where the employer has a mark, otherwise the employer name. */
  company: string;
  text: string;
  profilePic: string;
  /** Set where the raw mark needs correcting, e.g. a light logo on white. */
  logoFilter?: string;
} & LogoSizing;

/** CMA cards now carry the same shape, so they drop straight in. */
const cmaCards = cmaCardsRaw as TestimonialCard[];

const interleave = <T,>(a: T[], b: T[]): T[] => {
  const result: T[] = [];
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    if (i < a.length) result.push(a[i]);
    if (i < b.length) result.push(b[i]);
  }
  return result;
};

const NavButton = ({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={direction === "prev" ? "Previous story" : "Next story"}
    className={`hidden md:flex absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full border border-[#2f89ea] text-[#2f89ea] bg-white transition-colors hover:bg-[#2f89ea] hover:text-white ${
      direction === "prev" ? "left-0" : "right-0"
    }`}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      {direction === "prev" ? (
        <path d="M19 12H5m0 0 6-6m-6 6 6 6" />
      ) : (
        <path d="M5 12h14m0 0-6-6m6 6-6 6" />
      )}
    </svg>
  </button>
);

const CardItem = ({ card }: { card: TestimonialCard; uid: string }) => {
  const logoHeight = resolveLogoHeight(card);

  return (
    <article
      className="h-full flex flex-col items-center text-center bg-white rounded-2xl shadow-[0_5px_20px_-4px_rgba(0,0,0,0.12),0_0_8px_rgba(0,0,0,0.05)] px-4 py-4 max-md:px-4 max-md:py-4"
      aria-label={`Testimonial by ${card.name}`}
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- ImageKit URLs
        already carry their transforms, so no loader is needed. */}
      <img
        src={card.profilePic}
        alt={card.name}
        width={100}
        height={100}
        loading="lazy"
        decoding="async"
        className="w-[85px] h-[85px] md:w-[100px] md:h-[100px] rounded-full object-cover"
      />

      <h3 className="mt-4 text-[#3a73ac] text-base md:text-[20px] font-bold leading-tight">
        {card.name}
      </h3>
      {card.designation && (
        <p className="text-[#3a73ac] text-sm md:text-base font-semibold">
          {card.designation}
        </p>
      )}

      {/* Employers without a mark in the asset sheet get their name set in type
        in the same slot, so the row below it stays aligned. */}
      <span className={LOGO_SLOT_CLASS}>
        {isLogo(card.company) ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={logoSrc(card.company, logoHeight)}
            srcSet={logoSrcSet(card.company, logoHeight)}
            alt={card.name}
            loading="lazy"
            decoding="async"
            style={{
              maxHeight: logoHeight,
              ...(card.logoFilter ? { filter: card.logoFilter } : {}),
            }}
            className={LOGO_IMG_CLASS}
          />
        ) : (
          <span className="text-sm font-bold text-[#333]">{card.company}</span>
        )}
      </span>

      {/* Clamped because the quotes run from one line to four sentences, and
        items-stretch means the longest one would set the height of them all. */}
      <p className="mt-3 text-black text-xs md:text-sm leading-[22px] line-clamp-5">
        {card.text}
      </p>
    </article>
  );
};

interface TestimonialCitiesProps {
  pageName?: string;
  cards: TestimonialCard[];
  subTitle: string;
}

const TestimonialCities: React.FC<TestimonialCitiesProps> = ({
  pageName = "CPA",
  cards,
  subTitle,
}) => {
  const [isFormPopupOpen, setFormPopupOpen] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const isCombined = pageName === "CPA/CMA";
  const slides = isCombined ? interleave(cards, cmaCards) : cards;

  const scrollByCard = useCallback((direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    if (maxScroll <= 0) return;

    const card = track.firstElementChild as HTMLElement | null;
    const step = card ? card.offsetWidth : track.clientWidth / 3;

    let next = track.scrollLeft + direction * step;
    // 1px of tolerance for sub-pixel scroll positions.
    if (next > maxScroll + 1) {
      next = 0;
    } else if (next < -1) {
      next = maxScroll;
    }

    track.scrollTo({ left: next, behavior: "smooth" });
  }, []);

  const scrollPrev = useCallback(() => scrollByCard(-1), [scrollByCard]);
  const scrollNext = useCallback(() => scrollByCard(1), [scrollByCard]);

  const openFormPopup = (redirectUrl: string) => {
    const { status: isFormOneFilled } =
      getFormOneDataFromLocal(LOCAL_STORE_NAME);
    if (isFormOneFilled) {
      window.open(redirectUrl, "_blank");
      return;
    }
    sessionStorage.setItem("modal-open", "true");
    setFormPopupOpen(true);
  };

  const closeFormPopup = () => {
    sessionStorage.removeItem("modal-open");
    setFormPopupOpen(false);
  };

  const handleJoinCommunity = () =>
    openFormPopup("https://www.linkedin.com/school/mileseducation/");

  const handleEligibilityCheck = () =>
    openFormPopup("https://cpa-course.mileseducation.com/eligibility");
  return (
    <>
      <section
        className="max-md:px-4 max-md:pt-6 max-md:pb-0 bg_white_new"
        aria-labelledby="about-cpa-title"
      >
        <div className="main_container py-[50px] max-md:px-0 max-md:pt-5 max-md:pb-8 max-md:bg-white max-md:rounded-2xl">
          <h2
            id="about-cpa-title"
            className="m-0 text-center text-[32px] leading-[1.15] font-bold tracking-[-0.02em] text-[#333] max-md:text-2xl max-md:px-4"
          >
            Join the community of
            <span className="text-[#39F]"> 70,000+ Miles Alumni </span>
          </h2>
          <p className="max-w-[636px] mx-auto mt-4 text-center text-sm leading-[1.6] text-[#333] font-medium max-md:px-4 [&>span]:font-bold">
            {subTitle}
          </p>

          <div className="relative pt-8 max-md:pt-5 md:px-14">
            <NavButton direction="prev" onClick={scrollPrev} />

            <div
              ref={trackRef}
              className="scrollbar-hide flex items-stretch snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain px-4 md:px-0 md:mx-auto md:max-w-[1128px]"
            >
              {slides.map((card, i) => (
                <div
                  key={`story-${i}`}
                  className="w-[calc(100vw-64px)] md:w-[376px] shrink-0 snap-center md:snap-start px-2 md:px-3 py-4"
                >
                  <CardItem card={card} uid={`story-${i}`} />
                </div>
              ))}
            </div>

            <NavButton direction="next" onClick={scrollNext} />
          </div>

          <p className="md:hidden mt-2 text-center text-xs font-bold text-[#3A73AC]">
            &larr; Swipe to explore &rarr;
          </p>

          <div className="mt-8 max-md:mt-5 flex justify-center items-center gap-7 flex-wrap max-md:flex-col max-md:gap-3.5 max-md:px-5">
            <button
              onClick={handleJoinCommunity}
              className="text-[15px] font-bold no-underline cursor-pointer transition-all duration-200 hover:opacity-90 hover:-translate-y-px rounded-lg bg-[#2a85ff] text-white px-9 py-2.5 max-md:text-base max-md:w-full max-md:py-3 max-md:text-center max-md:box-border"
            >
              Join the Community
            </button>

            <button
              onClick={handleEligibilityCheck}
              className="text-[15px] font-bold cursor-pointer transition-all duration-200 hover:opacity-90 hover:-translate-y-px text-[#2A85FF] underline underline-offset-4 max-md:text-sm"
            >
              Get a Free Eligibility Check
            </button>
          </div>
        </div>
      </section>

      {isFormPopupOpen && (
        <PopupForm handleClose={closeFormPopup} pageName={pageName} />
      )}
    </>
  );
};

export default TestimonialCities;
